import { useEffect, useState } from 'react';
import { DataTable, Column, Pagination } from 'fluent-components';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/components/hooks/use-toast';
import { Escalation } from '@/types/doctorManagementTypes';
import {
  newPermissionsFeatureCodes,
  taskQueryBusinessStatus,
} from '@/constants';
import useUserInfo from '@/components/Authenticate/useUserInfo';
import { Task } from '@/types/recordManagement';
import { getPractitionerId, transformEscalationData } from '@/lib/utils';
import { Loader } from 'lucide-react';

import { documentReferenceAPIs } from '@/lib/axios/apis/recordManagement/document';

const getColumns = (): Column<Escalation>[] => {
  return [
    {
      key: 'escalatedBy',
      label: 'Escalated by',
      type: 'text',
    },
    {
      key: 'issue',
      label: 'Issue',
      type: 'text',
    },
    {
      key: 'dateTime',
      label: 'Date & Time',
      type: 'text',
    },
  ];
};

export default function EscalationListPage() {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filteredData, setFilteredData] = useState<Task[] | null>(null);
  const [escalatedData, setEscalatedData] = useState<Escalation[] | null>(null);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const { userInfo, userPermissions } = useUserInfo();
  const practitionerId = getPractitionerId(userInfo);
  const offset = (currentPage - 1) * pageSize;

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const combinedStatus = [
        taskQueryBusinessStatus?.documentSupervisorBusinessStatus,
        taskQueryBusinessStatus?.documentSupervisorFallbackBusinessStatus,
      ].join(',');
      
      const primaryResponse = await documentReferenceAPIs.getTasks(
        pageSize,
        '_lastUpdated',
        'accurate',
        offset,
        practitionerId,
        combinedStatus
      );
      setFilteredData(primaryResponse?.tasks ?? []);
      setTotalItems(primaryResponse?.total ?? 0);
    } catch (err) {
      console.error({ err });
      toast({
        title: 'Error',
        description: 'Failed to fetch tasks. Please try again later.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  //TODO: Keeping this function for future use if needed - 
  // Once tested the new flow will remove this

  // const fetchCombinedTaskCount = async () => {
  //   try {
  //     // Fetch primary task count first
  //     const primary = await getData(
  //       `${URL_PREFIX}/Task?_summary=count&business-status=${taskQueryBusinessStatus?.documentSupervisorBusinessStatus}&owner=Practitioner/${practitionerId}`
  //     );
  //     const fallback = await getData(
  //       `${URL_PREFIX}/Task?_summary=count&business-status=${taskQueryBusinessStatus?.documentSupervisorFallbackBusinessStatus}&owner=Practitioner/${practitionerId}`
  //     );
  //     console.log(primary, fallback);
  //     const total = (primary?.total ?? 0) + (fallback?.total ?? 0);
  //     setTotalItems(total);
  //   } catch (error) {
  //     console.error('Failed to fetch task count:', error);
  //     toast({
  //       title: 'Error',
  //       description: 'Failed to fetch task count. Please try again later.',
  //       variant: 'destructive',
  //     });
  //   }
  // };

  useEffect(() => {
    fetchTasks();
    localStorage.removeItem('reviewStarted');
    localStorage.removeItem('validationStarted');
  }, [currentPage, pageSize]);

  useEffect(() => {
    if (!filteredData?.length) return setEscalatedData([]);
    const transformedObject = transformEscalationData(filteredData);
    if (transformedObject) {
      setEscalatedData(transformedObject);
    }
  }, [filteredData]);

  const navigate = useNavigate();

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to the first page
  };

  const handleRowClick = (escalation: Escalation) => {
    const hasPermission =
      userPermissions?.[newPermissionsFeatureCodes.quality_check_conduct] ===
      'CHECK_RESULT_ALLOWED';
    if (hasPermission) {
      navigate(`/supervisor/escalation/${escalation.id}`, {
        state: { escalation },
      });
    } else {
      toast({
        title: 'Warning',
        description: `You don't have access!`,
        variant: 'destructive',
      });
    }
  };

  const columns = getColumns();

  if (loading) {
    return <Loader className="fixed left-1/2 top-1/2 animate-spin h-10 w-10" />;
  }

  return (
    <div className="h-[calc(100vh)]">
      <div className="overflow-y-auto h-3/5">
        <DataTable<Escalation>
          data={escalatedData ?? []}
          columns={columns}
          onRowClick={handleRowClick}
        />
      </div>

      <div className="flex justify-end mt-10">
        <Pagination
          totalItems={totalItems}
          currentPage={currentPage}
          pageSize={pageSize}
          showItemsPerPageDropdown={false}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      </div>
    </div>
  );
}
